/**
 * Wallet Data Context
 *
 * Unified provider that wraps all wallet data contexts:
 * - WalletSummaryProvider: Balance and totals (real-time from wallet/summary)
 * - WalletEarningsProvider: Earnings transactions
 * - WalletTransactionsProvider: Payments and deposits
 * - WalletExpensesProvider: Expense transactions
 * - WalletUsageProvider: Usage transactions
 *
 * Use this at the app/page level to enable wallet data sync for all child components.
 */

import React from 'react';
import { WalletEarningsProvider } from './WalletEarningsContext';
import { WalletExpensesProvider } from './WalletExpensesContext';
import { WalletUsageProvider } from './WalletUsageContext';
import { WalletTransactionsProvider } from './WalletTransactionsContext';

// Re-export hooks for convenience
export { useWalletSummary, useMaybeWalletSummary } from './WalletSummaryContext';
export { useWalletEarnings, useMaybeWalletEarnings } from './WalletEarningsContext';
export { useWalletExpenses, useMaybeWalletExpenses } from './WalletExpensesContext';
export { useWalletUsage, useMaybeWalletUsage } from './WalletUsageContext';
export { useWalletTransactions, useMaybeWalletTransactions } from './WalletTransactionsContext';

// Re-export types for convenience
export type { AddEarningInput } from './WalletEarningsContext';
export type { AddExpenseInput } from './WalletExpensesContext';
export type { AddUsageInput } from './WalletUsageContext';
export type { AddTransactionInput } from './WalletTransactionsContext';

interface WalletDataProviderProps {
  children: React.ReactNode;
}

/**
 * Unified provider that wraps all wallet data contexts.
 * Enables real-time Firestore sync for earnings, transactions, expenses, and usage.
 *
 * Usage:
 * ```tsx
 * <WalletDataProvider>
 *   <WalletPage />
 * </WalletDataProvider>
 * ```
 */
export function WalletDataProvider({ children }: WalletDataProviderProps) {
  return (
    <WalletEarningsProvider>
      <WalletTransactionsProvider>
        <WalletExpensesProvider>
          <WalletUsageProvider>
            {children}
          </WalletUsageProvider>
        </WalletExpensesProvider>
      </WalletTransactionsProvider>
    </WalletEarningsProvider>
  );
}

export default WalletDataProvider;
